import { Container } from "aurelia-dependency-injection";
import { defineComponent, PropType } from "vue";

import { PatientService } from "../../../../../../services/patient.service";
import { IResolvedProblems } from "../../../../../../resources-vue/vue-interfaces/i-patient";
import { ITypeaheadOptions } from "../../../../../../resources-vue/vue-interfaces/i-typeahead";
import { LookupService } from "../../../../../../services/lookup-service";
import { formatDate, formatDateInput } from "../../../../../../common/vue-helpers/modifiers/value-modifier";
import { IProblems } from "../../../../../../resources-vue/vue-interfaces/i-problems";
import { ApplicationEnum } from "../../../../../../enums/ApplicationEnum";

import TypeaheadInput from "../../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import DatePicker from "../../../../../../resources-vue/vue-custom-elements/DatePicker/DatePicker.vue";
import TablePrompt from "../../../../../../resources-vue/vue-custom-elements/TablePrompt/TablePrompt.vue";
import EmptyTextHandler from "../../../../../../resources-vue/vue-custom-elements/EmptyTextHandler/EmptyTextHandler.vue";

export default defineComponent({
    components: {
        TypeaheadInput,
        DatePicker,
        EmptyTextHandler,
        TablePrompt
    },

    props: {
        problem: {
            type: Object as PropType<IResolvedProblems>,
            default: null
        },
        isAddRequested: {
            type: Boolean,
            default: false
        },
        cancelSaveNewProblem: {
            type: Function
        },
        saveNewProblem: {
            type: Function
        },
        moveProblemDown: {
            type: Function
        },
        moveProblemUp: {
            type: Function
        },
        first: {
            type: Boolean
        },
        last: {
            type: Boolean
        },
        columns: {
            type: Number
        },
        problemsLength: {
            type: Number
        },
        index: {
            type: Number
        },
        currentView: {
            type: String,
            default: "edit"
        },
        deleteProblem: {
            type: Function
        }
    },

    data() {
        return {
            selectedProblem: null as ITypeaheadOptions,
            problemCache: null as IProblems,
            isEditRequested: false as boolean,
            isDeleteRequested: false as boolean,
            deleteMessage: `Do you want to delete this problem?` as string,
            _patientsService: null as PatientService,
            _lookupService: null as LookupService,
            formatDateInput,
            formatDate
        };
    },

    emits: ["update:isAddRequested", "cancelEditProblem"],

    created() {
        this._patientsService = Container.instance.get(PatientService);
        this._lookupService = Container.instance.get(LookupService);
    },

    watch: {
        selectedProblem() {
            this.selectedProblemChanged();
        }
    },

    methods: {
        handleProblemChange(newValue: ITypeaheadOptions) {
            if (newValue) {
                this.selectedProblem = { ...newValue };
            }
        },
        startDateUnknownChanged(newValue: boolean, oldValue: boolean) {
            if (!!newValue && !oldValue) {
                this.problem.startDate = null;
            }
        },

        resolvedDateUnknownChanged(newValue: boolean, oldValue: boolean) {
            if (!!newValue && !oldValue) {
                this.problem.resolvedDate = null;
            }
        },

        async save() {
            this.$emit("update:isAddRequested", false);
            this.problemCache = { ...this.problem };
            this.selectedProblem = null;
            this.saveNewProblem(this.index);
        },

        selectedProblemChanged() {
            if (!!this.selectedProblem) {
                this.problem.code = this.selectedProblem.value;
                this.problem.description = this.selectedProblem.name;
            }
        },

        async getProblemsList(filter: string, limit: number) {
            const payload = {
                term: filter,
                application: ApplicationEnum.AxxessPalliative
            };
            let data = await this._lookupService.getProblems(payload);
            if (data) {
                return data.map((problem: any) => ({
                    name: problem.Description,
                    value: problem.Code
                }));
            }
            return [];
        },

        openEditProblem() {
            this.problemCache = { ...this.problem };
            if (this.problem.code !== null) {
                this.selectedProblem = {
                    value: this.problem.code,
                    name: this.problem.description
                };
            }
            this.isEditRequested = true;
        },

        cancelEditProblem() {
            this.isEditRequested = false;
            this.$emit("cancelEditProblem", { ...this.problemCache }, this.index);
            this.selectedProblem = null;
        },

        async saveEditProblem() {
            this.problemCache = { ...this.problem };
            this.selectedProblem = null;
            this.isEditRequested = false;
        },

        openDeleteProblem() {
            this.isDeleteRequested = true;
        },

        cancelDeleteRequest() {
            this.isDeleteRequested = false;
        },

        handleDateChange(date: string, name: string) {
            if (name === "resolvedDate") {
                this.problem.resolvedDate = date;
            }
            if (name === "startDate") {
                this.problem.startDate = date;
            }
        },
        handleDateInputChange(e: any) {
            if (e.target.name === "resolvedDate") {
                this.problem.resolvedDate = e.target.value;
            }
            if (e.target.name === "startDate") {
                this.problem.startDate = e.target.value;
            }
        },
        handleDeleteProblem() {
            this.deleteProblem(this.index);
        }
    }
});
